// Small UI helpers for the register (money format, variant parsing, add-to-cart payload).
import { resolveTaxList, toModel } from "./data/queries";

export const fmt = (n) => "$" + (Math.round((Number(n) || 0) * 100) / 100).toFixed(2);

const split = (s) => String(s == null ? "" : s).split(",");

// Expand a product's comma-joined variant fields into pickable variants (incl. per-variant stock).
export function parseVariants(product) {
  if (String(product.isvarient) !== "1") return [];
  const names = split(product.vvariant);
  const prices = split(product.vprice);
  const ids = split(product.vvariant_id);
  const upcs = split(product.vupc);
  const codes = split(product.vcustom_code); // per-variant slot; each slot may be pipe-delimited (multi-code)
  const qtys = split(product.vquantity);
  const tracks = split(product.vtrackqnty);
  const conts = split(product.visstockcontinue);
  const pqs = split(product.vpurchase_qty); // per-transaction cap (0/blank = unlimited)
  const disables = split(product.vdisable); // per-variant disabled flag
  const tobaccos = split(product.vis_tobacco); // per-variant 21+ (tobacco) flag
  const medias = split(product.vmedia); // Android 7494508ccf vMedia — per-variant image, index-aligned
  const out = [];
  for (let i = 0; i < names.length; i++) {
    const nm = (names[i] || "").trim();
    if (!nm || nm.toLowerCase() === "null") continue;
    if (String((disables[i] || "").trim()) === "1") continue; // Android isDisable(): skip disabled variants
    out.push({
      variantId: (ids[i] || "").trim(),
      name: nm,
      price: Number(prices[i]) || 0,
      upc: (upcs[i] || "").trim(),
      customCodes: String(codes[i] || "").split("|").map((c) => c.trim()).filter(Boolean),
      quantity: Number(qtys[i]) || 0,
      track: (tracks[i] || "").trim(),
      continueSell: (conts[i] || "").trim(),
      purchaseQty: Number(pqs[i]) || 0,
      disable: (disables[i] || "").trim(),
      isTobacco: (tobaccos[i] || "").trim(),
      media: (medias[i] || "").trim(),
    });
  }
  return out;
}

// Display/order label — product title joined with the variant name (POS shows "title-variant").
export function itemLabel(product, variant) {
  return variant ? `${product.title}-${variant.name}` : product.title;
}

// Stock snapshot for a product (+ optional variant). `tracked` = inventory tracked AND "continue
// selling when out" is off — the only case where quantity limits adds (mirrors getShouldAddToCart).
export function stockOf(product, variant) {
  const track = variant ? variant.track : product.trackqnty;
  const cont = variant ? variant.continueSell : product.isstockcontinue;
  const qty = variant ? variant.quantity : Number(product.quantity) || 0;
  return { tracked: String(track) === "1" && String(cont) !== "1", qty: Number(qty) || 0 };
}

// POS tile accent colors (cat_color_1..5), cycled by grid position — matches InStoreProductsListAdapter.
export const TILE_COLORS = ["#E3A735", "#5D49F5", "#D935E9", "#15AE5D", "#E33535"];

// Next auto-numbered custom-line name for a base ("Custom Item"→"Custom Item 1/2/3", "Vapes"→"Vapes 1..").
// Uses max-existing-suffix + 1 so deleting a middle line never produces a duplicate name.
// Android OrderCreateDataModel.isCustomItem(i): each UNIT of a custom/category-as-item line is named
// `${base} ${count + 1}` where count = cart lines whose name is the base or starts with "base " —
// counted against the cart AS IT GROWS, so a 3x add yields "Custom Item 1/2/3".
// A category-as-item line counts by a STABLE key instead: custom lines (product id 0) carrying the
// same category id. `lines` may be cart lines (objects) or plain names (strings) for the name rule.
export function androidCustomLineName(lines, baseName, categoryId = "") {
  const base = String(baseName || "Item").trim() || "Item";
  const arr = lines || [];
  let count;
  if (String(categoryId || "").trim()) {
    const cid = String(categoryId).trim();
    count = arr.filter((l) => l && typeof l === "object" && String(l.orderCategoryId || "").trim() === cid && (l.isCustomLineItem || String(l.orderProductId) === "0")).length;
  } else {
    const prefix = (base + " ").toLowerCase();
    count = arr.filter((l) => {
      const s = String(l && typeof l === "object" ? l.orderName : l == null ? "" : l).trim();
      return s === base || s.toLowerCase().startsWith(prefix);
    }).length;
  }
  return `${base} ${count + 1}`;
}

export function nextCustomLineName(names, baseName) {
  const base = String(baseName || "Item").trim() || "Item";
  const esc = base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp("^" + esc + "(?: (\\d+))?$");
  let max = 0;
  for (const n of names || []) {
    const m = re.exec(String(n == null ? "" : n));
    if (m) max = Math.max(max, m[1] ? Number(m[1]) : 1);
  }
  return `${base} ${max + 1}`;
}

// Numeric amount pad (POS `tvCustomItemPrice` / `sb`): a raw digit buffer READ AS CENTS. Max 8 digits
// → max $999,999.99. No decimal key (cents implied): typing 1,2,5 → "125" → $1.25. Feeds Custom Item
// price + OTD amount (getInputAmount). C clears, DEL backspaces.
export function applyAmountKey(buffer, key) {
  const b = String(buffer || "");
  if (key === "C") return "";
  if (key === "DEL") return b.slice(0, -1);
  if (/^[0-9]$/.test(key)) return b.length < 8 ? b + key : b;
  return b;
}
export function amountFromBuffer(buffer) {
  return (Number(String(buffer || "").replace(/\D/g, "")) || 0) / 100;
}
export function formatAmountBuffer(buffer) {
  return "$" + amountFromBuffer(buffer).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Category tile accent color — ports resolveCategoryAccentColor: use the API `color` if it's a valid
// hex, else a known-title map, else a palette by index. (The POS's device-local saved-color step has
// no web equivalent and is skipped.)
const CAT_FALLBACK_PALETTE = ["#EF4444", "#F97316", "#F59E0B", "#22C55E", "#14B8A6", "#0EA5E9", "#3B82F6", "#A855F7", "#EC4899", "#64748B"];
const CAT_KNOWN = { quickadd: "#22C55E", vapes: "#3B82F6", grinders: "#A855F7", vaporizers: "#06B6D4", bongs: "#EF4444", cigarettes: "#F59E0B", kratom: "#8B5CF6", accessories: "#64748B", apparel: "#EC4899", "snacks & drinks": "#10B981", lottery: "#DC2626" };

export function normalizeHexColor(value) {
  const trimmed = String(value == null ? "" : value).trim();
  if (!trimmed) return null;
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  return /^#[0-9a-fA-F]{6}$/.test(withHash) ? withHash.toUpperCase() : null;
}

export function categoryAccentColor(apiColor, title, index) {
  const fromApi = normalizeHexColor(apiColor);
  if (fromApi) return fromApi;
  const known = CAT_KNOWN[String(title || "").trim().toLowerCase()];
  if (known) return known;
  const p = CAT_FALLBACK_PALETTE;
  return p[(((index || 0) % p.length) + p.length) % p.length];
}

// Build the cartSlice.addItem payload for a product (+ optional chosen variant + quantity multiplier).
// `bulk` = the product/variant's Mix&Match deal (from mixMatchFor) — joins bpId/bulkQuantity/bulkPrice
// onto the model so the engine's validateBulkPrice can apply the group discount.
export function buildAddPayload(product, variant, taxMap, qty = 1, bulk = null) {
  const st = stockOf(product, variant);
  return {
    productId: product.id,
    name: itemLabel(product, variant),
    unitPrice: variant ? variant.price : Number(product.price) || 0,
    isVariant: !!variant,
    variantId: variant ? variant.variantId : "",
    // The chosen variant's OWN scan code. Must be stamped here: `model.vupc` is the comma-joined list
    // of EVERY variant's code, so nothing downstream can recover which one this line is.
    variantUpc: variant ? variant.upc : "",
    variantMedia: variant ? variant.media || "" : "", // item_ses.img uses the variant's own image
    taxList: resolveTaxList(product, taxMap),
    model: toModel(product, bulk),
    isLotteryTicket: String(product.is_lottery) === "1" ? "1" : "0",
    stockTracked: st.tracked, // cap qty at maxStock when true
    maxStock: st.qty,
    // Per-transaction purchase limit (validatePurchaseQty): variant vpurchase_qty else product
    // purchase_qty; 0 = unlimited.
    purchaseQtyCap: Number(variant ? variant.purchaseQty : product.purchase_qty) || 0,
    qty: Number(qty) > 0 ? Number(qty) : 1,
  };
}
